/**
 * Spec 089b — Search Console Overview panel.
 *
 * Mounted on /overview between "Visibility by engine" and "Detected gaps".
 *
 * State machine (mutually exclusive, derived from API HTTP statuses):
 *   - loading      → spinner skeleton
 *   - flag-off     → renders nothing on Overview (spec 250); remembered for the session
 *   - no-connection → "Connect Search Console" CTA pointing at /settings?tab=integrations
 *   - no-metrics   → connected but 0 AI-surface rows in the last 28 days
 *   - metrics      → full KPI tile + per-surface breakdown + top-10 queries table
 *
 * Signals (all via apiFetch — 401 redirects to the sign-in sheet, /signin):
 *   GET .../search-console/connection   → 503 flag-off / 404 no-conn / 200 connected
 *   GET .../search-console/metrics?from=<-56d>&to=<today>
 */

import { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router';
import { Icon, Spinner } from './ui/Icon.js';
import { apiFetch } from '../lib/api.js';
import { apiPost } from '../lib/api-mutations.js';
import { useAuth } from '../lib/auth-context.js';
import { useToast } from '../hooks/useToast.js';

const FLAG_OFF_KEY = 'geo.gsc.flag-off';
function readFlagOff(): boolean {
  try { return sessionStorage.getItem(FLAG_OFF_KEY) === '1'; } catch { return false; }
}
function rememberFlagOff(): void {
  try { sessionStorage.setItem(FLAG_OFF_KEY, '1'); } catch { /* private mode: ask again next load */ }
}

interface SearchConsolePanelProps {
  brandId: string;
  /** Optional — currently unused but kept for parity with other Overview sections. */
  tenantId?: string | null;
}

interface ConnectionSummary {
  status: 'active' | 'expired' | 'revoked' | 'error';
  site_url: string;
  last_sync_at: string | null;
  last_sync_error: string | null;
}

interface MetricRow {
  date: string;
  query: string;
  page: string;
  country: string;
  device: 'desktop' | 'mobile' | 'tablet';
  search_appearance: 'all' | 'ai_overview' | 'ai_mode' | 'featured_snippet' | 'other';
  impressions: number;
  clicks: number;
  ctr: number | null;
  position: number | null;
}

type PanelState =
  | { kind: 'loading' }
  | { kind: 'flag-off' }
  | { kind: 'no-connection' }
  // Spec 091 — surface sync errors instead of the misleading "no impressions"
  // empty state. Reached when the /connection response has status='error'
  // and a populated last_sync_error.
  | { kind: 'error'; connection: ConnectionSummary }
  | {
      kind: 'connected';
      connection: ConnectionSummary;
      metrics: MetricRow[];
    };

const AI_SURFACES = ['ai_overview', 'ai_mode', 'featured_snippet'] as const;
type AiSurface = (typeof AI_SURFACES)[number];

function daysAgoIso(n: number): string {
  return new Date(Date.now() - n * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

function formatPct(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return `${(n * 100).toFixed(1)}%`;
}

function formatTrend(current: number, prior: number): {
  arrow: 'up' | 'down' | 'flat' | 'new' | 'none';
  label: string;
} {
  if (prior === 0 && current === 0) return { arrow: 'none', label: '—' };
  if (prior === 0 && current > 0) return { arrow: 'up', label: 'new' };
  if (prior > 0 && current === 0) return { arrow: 'down', label: '-100.0%' };
  const pct = ((current - prior) / prior) * 100;
  const clamped = Math.max(-999.9, Math.min(999.9, pct));
  if (Math.abs(clamped) < 0.05) return { arrow: 'flat', label: '0.0%' };
  return {
    arrow: clamped > 0 ? 'up' : 'down',
    label: `${clamped > 0 ? '+' : ''}${clamped.toFixed(1)}%`,
  };
}

interface BackfillStatus {
  in_progress: boolean;
  target_date: string | null;
  progress_date: string | null;
  percent_complete: number | null;
}

function daysBetween(laterIso: string, earlierIso: string): number {
  const a = new Date(`${laterIso}T00:00:00Z`).getTime();
  const b = new Date(`${earlierIso}T00:00:00Z`).getTime();
  return Math.max(0, Math.round((a - b) / (24 * 60 * 60 * 1000)));
}

export function SearchConsolePanel({ brandId, tenantId }: SearchConsolePanelProps) {
  const [state, setState] = useState<PanelState>({ kind: 'loading' });
  const [syncing, setSyncing] = useState(false);
  const [backfill, setBackfill] = useState<BackfillStatus | null>(null);
  const { hasRole } = useAuth();
  const toast = useToast();

  // Connect/Disconnect/Sync buttons hidden for viewers.
  const canMutate =
    hasRole('brand-owner') ||
    hasRole('brand-member') ||
    hasRole('orbit:admin') ||
    hasRole('orbit:developer');

  const loadAll = useCallback(async () => {
    // Spec 250 (review P2): an unconfigured integration is not asked again on
    // every Overview load. A 503 means the subsystem is off for the whole
    // deployment, so it is remembered for the session.
    if (readFlagOff()) {
      setState({ kind: 'flag-off' });
      return;
    }
    setState({ kind: 'loading' });
    // Probe the connection summary first — its status code drives the state machine.
    const connRes = await apiFetch(`/api/v1/brands/${brandId}/search-console/connection`);
    if (connRes.status === 503) {
      rememberFlagOff();
      setState({ kind: 'flag-off' });
      return;
    }
    if (connRes.status === 404) {
      setState({ kind: 'no-connection' });
      return;
    }
    if (!connRes.ok) {
      // Defensive — treat unknown failure as a soft no-connection so the page
      // never errors out the whole Overview. The user can still navigate.
      // eslint-disable-next-line no-console
      console.warn('SearchConsolePanel: connection fetch failed', connRes.status);
      setState({ kind: 'no-connection' });
      return;
    }
    const connection = (await connRes.json()) as ConnectionSummary;

    // Spec 091 — surface sync errors instead of rendering a misleading
    // "no impressions" empty state. When the backend marked the connection
    // as errored, show the actual error text and a retry affordance.
    if (connection.status === 'error' && connection.last_sync_error) {
      setState({ kind: 'error', connection });
      return;
    }

    // Fetch metrics for the last 56 days so we can compute current vs prior window.
    const from = daysAgoIso(56);
    const to = daysAgoIso(0);
    const metricsRes = await apiFetch(
      `/api/v1/brands/${brandId}/search-console/metrics?from=${from}&to=${to}&limit=1000`,
    );
    if (!metricsRes.ok) {
      // Connection exists but metrics fetch failed — render the connected state
      // with empty metrics. The "no metrics" hint will appear.
      setState({ kind: 'connected', connection, metrics: [] });
      return;
    }
    const metricsBody = (await metricsRes.json()) as { data: MetricRow[] };
    setState({ kind: 'connected', connection, metrics: metricsBody.data });
  }, [brandId]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  // Spec 089e — fetch backfill status once. Non-blocking. Spec 250: only once a
  // connection exists — with none (or the subsystem off) it could only 404/503.
  const hasConnection = state.kind === 'connected' || state.kind === 'error';
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!tenantId || !hasConnection) return;
      const res = await apiFetch(
        `/api/v1/tenants/${tenantId}/brands/${brandId}/search-console/backfill/status`,
      );
      if (!res.ok || cancelled) return;
      const body = (await res.json()) as BackfillStatus;
      if (!cancelled) setBackfill(body);
    })();
    return () => {
      cancelled = true;
    };
  }, [brandId, tenantId, hasConnection]);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    try {
      await apiPost(`/api/v1/brands/${brandId}/search-console/sync`, {});
      toast.addToast('Sync queued — data will refresh on the next cron tick', 'success');
      // Reload after a short delay so last_sync_at picks up if the worker runs immediately.
      setTimeout(() => void loadAll(), 1500);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('429') || msg.toLowerCase().includes('hour')) {
        toast.addToast('Sync rate-limited — please wait an hour between manual syncs', 'error');
      } else {
        toast.addToast(`Sync failed: ${msg.slice(0, 120)}`, 'error');
      }
    } finally {
      setSyncing(false);
    }
  }, [brandId, loadAll, toast]);

  // ─── Render ──────────────────────────────────────────────────────
  if (state.kind === 'loading') {
    return (
      <PanelShell>
        <div className="flex items-center gap-2 p-6 text-sm text-slate-500" data-testid="gsc-panel-loading">
          <Spinner  />
          Loading Search Console data…
        </div>
      </PanelShell>
    );
  }

  // Spec 250 (review P2): with the subsystem off there is nothing to show on
  // Overview — its state belongs in Settings, not in an amber panel on every
  // dashboard load. The marker keeps the state testable.
  if (state.kind === 'flag-off') {
    return <span hidden data-testid="gsc-panel-flag-off" />;
  }

  if (state.kind === 'no-connection') {
    return (
      <PanelShell>
        <div
          data-testid="gsc-panel-cta"
          className="hub-plate hub-plate--flat flex flex-col items-start gap-3 p-6"
        >
          <div className="flex items-center gap-2 text-slate-700">
            <Icon name="bx-plug" />
            <span className="font-medium">Connect Search Console</span>
          </div>
          <p className="text-sm text-slate-500 max-w-2xl">
            See how Google's real users discover your brand in AI Overviews and AI
            Mode. Connect this brand to your Search Console property to surface
            ground-truth impressions, clicks, CTR, and average position alongside
            GEO Observatory's monitoring metrics.
          </p>
          <Link
            to="/settings?tab=integrations"
            className="hub-btn hub-btn--ghost"
          >
            <Icon name="bx-plug" />
            Connect Search Console
          </Link>
        </div>
      </PanelShell>
    );
  }

  // Spec 091 — sync-error tile. Distinct from the "no impressions yet"
  // empty state so a real Google API failure (HTTP 400 dimension error,
  // OAuth revoked, quota exceeded) does not look like "wait until tomorrow".
  if (state.kind === 'error') {
    const errorText = state.connection.last_sync_error ?? 'Unknown sync error';
    // Truncate inline view at 280 chars; full text is exposed via the
    // native title= attribute so operators can hover/copy the verbatim
    // message without an extra click.
    const inlineText =
      errorText.length > 280 ? `${errorText.slice(0, 277)}...` : errorText;
    return (
      <PanelShell
        headerRight={
          canMutate && (
            <button
              type="button"
              onClick={() => void handleSync()}
              disabled={syncing}
              data-testid="gsc-panel-error-retry"
              className="hub-btn hub-btn--ghost"
            >
              {syncing ? <Spinner  /> : <Icon name="bx-redo" />}
              Retry sync
            </button>
          )
        }
      >
        <div
          data-testid="gsc-panel-error"
          className="flex items-start gap-3 rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900"
        >
          <Icon name="bx-error-circle" className="mt-0.5 flex-shrink-0 text-rose-600" />
          <div className="min-w-0 flex-1">
            <p className="font-medium text-rose-900">Search Console sync failed</p>
            <p
              className="mt-1 text-rose-800 break-words"
              title={errorText}
              data-testid="gsc-panel-error-text"
            >
              {inlineText}
            </p>
            <p className="mt-2 text-xs text-rose-700/80">
              GEO Observatory could not retrieve impressions on the most recent sync attempt.
              {canMutate ? ' Use Retry sync above (or wait for the next cron tick).' : ' Contact your GEO Observatory admin.'}
            </p>
          </div>
        </div>
      </PanelShell>
    );
  }

  // ─── Connected ──────────────────────────────────────────────────
  return (
    <PanelShell
      headerRight={
        <div className="flex items-center gap-3 text-xs text-slate-500">
          {state.connection.last_sync_at && (
            <span data-testid="gsc-last-synced">
              Last synced {formatRelativeTime(state.connection.last_sync_at)}
            </span>
          )}
          {canMutate && (
            <button
              type="button"
              onClick={() => void handleSync()}
              disabled={syncing}
              data-testid="gsc-sync-now"
              className="hub-btn hub-btn--ghost"
            >
              {syncing ? <Spinner  /> : <Icon name="bx-redo" />}
              Sync now
            </button>
          )}
        </div>
      }
    >
      {backfill?.in_progress && backfill.target_date && backfill.progress_date && (
        <div
          data-testid="gsc-overview-backfill-badge"
          className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-xs font-medium text-violet-700"
        >
          <Icon name="bx-redo" className="animate-spin" />
          Backfilling history — {daysBetween(backfill.progress_date, backfill.target_date)} days remaining
        </div>
      )}
      <ConnectedBody metrics={state.metrics} />
    </PanelShell>
  );
}

// ─── Sub-renderers ────────────────────────────────────────────────

function PanelShell({
  children,
  headerRight,
}: {
  children: React.ReactNode;
  headerRight?: React.ReactNode;
}) {
  return (
    <div data-testid="search-console-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon name="bx-globe" className="text-violet-600" />
          <h2 className="text-lg font-semibold text-slate-900">
            Google AI surfaces (Search Console)
          </h2>
        </div>
        {headerRight}
      </div>
      <p className="mt-1 text-xs text-slate-400">
        Comparison window is fixed at the last 28 days vs the prior 28 days,
        independent of the page-level date selector.
      </p>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function ConnectedBody({ metrics }: { metrics: MetricRow[] }) {
  // Split into current 28d (days -27..0) and prior 28d (days -55..-28). Date
  // is stored as YYYY-MM-DD; we compute window boundaries inclusively.
  const { current, prior, topQueries, byEngineCurrent } = useMemo(() => {
    const today = daysAgoIso(0);
    const start28 = daysAgoIso(27);
    const startPrior = daysAgoIso(55);
    const endPrior = daysAgoIso(28);

    const aiCurrent: MetricRow[] = [];
    const aiPrior: MetricRow[] = [];
    for (const row of metrics) {
      if (!AI_SURFACES.includes(row.search_appearance as AiSurface)) continue;
      if (row.date >= start28 && row.date <= today) aiCurrent.push(row);
      else if (row.date >= startPrior && row.date <= endPrior) aiPrior.push(row);
    }

    const sumImpressions = (rows: MetricRow[]) =>
      rows.reduce((acc, r) => acc + r.impressions, 0);
    const sumClicks = (rows: MetricRow[]) =>
      rows.reduce((acc, r) => acc + r.clicks, 0);
    const avgPosition = (rows: MetricRow[]) => {
      const totalImpr = sumImpressions(rows);
      if (totalImpr === 0) return null;
      // Weighted by impressions per Google's convention.
      const numer = rows.reduce(
        (acc, r) => acc + (r.position ?? 0) * r.impressions,
        0,
      );
      return numer / totalImpr;
    };

    const current = {
      impressions: sumImpressions(aiCurrent),
      clicks: sumClicks(aiCurrent),
    };
    const prior = {
      impressions: sumImpressions(aiPrior),
      clicks: sumClicks(aiPrior),
    };

    // Per-surface tiles for current window.
    const byEngineCurrent: Record<AiSurface, { impressions: number; clicks: number; ctr: number; position: number | null }> = {
      ai_overview: { impressions: 0, clicks: 0, ctr: 0, position: null },
      ai_mode: { impressions: 0, clicks: 0, ctr: 0, position: null },
      featured_snippet: { impressions: 0, clicks: 0, ctr: 0, position: null },
    };
    for (const surface of AI_SURFACES) {
      const rows = aiCurrent.filter((r) => r.search_appearance === surface);
      const impressions = sumImpressions(rows);
      const clicks = sumClicks(rows);
      byEngineCurrent[surface] = {
        impressions,
        clicks,
        ctr: impressions === 0 ? 0 : clicks / impressions,
        position: avgPosition(rows),
      };
    }

    // Top-10 queries by impressions across all AI surfaces in current window.
    const queryAgg = new Map<
      string,
      { query: string; impressions: number; clicks: number; positionSum: number; positionWeight: number }
    >();
    for (const r of aiCurrent) {
      const key = r.query;
      const cur = queryAgg.get(key) ?? {
        query: key,
        impressions: 0,
        clicks: 0,
        positionSum: 0,
        positionWeight: 0,
      };
      cur.impressions += r.impressions;
      cur.clicks += r.clicks;
      cur.positionSum += (r.position ?? 0) * r.impressions;
      cur.positionWeight += r.impressions;
      queryAgg.set(key, cur);
    }
    const topQueries = Array.from(queryAgg.values())
      .sort((a, b) => b.impressions - a.impressions)
      .slice(0, 10)
      .map((q) => ({
        query: q.query,
        impressions: q.impressions,
        clicks: q.clicks,
        ctr: q.impressions === 0 ? 0 : q.clicks / q.impressions,
        position: q.positionWeight === 0 ? null : q.positionSum / q.positionWeight,
      }));

    return { current, prior, topQueries, byEngineCurrent };
  }, [metrics]);

  const trend = formatTrend(current.impressions, prior.impressions);

  // No-metrics sub-state — connected but 0 AI-surface rows.
  if (current.impressions === 0 && prior.impressions === 0 && topQueries.length === 0) {
    return (
      <div
        data-testid="gsc-panel-no-metrics"
        className="rounded-md border border-slate-200 bg-slate-50/40 p-6 text-sm text-slate-600"
      >
        No AI-surface impressions in the last 28 days — your brand may not yet be
        appearing in Google AI Overviews or AI Mode for any queries that drove user
        impressions. Sync runs daily; check back tomorrow.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="gsc-panel-connected">
      {/* Headline KPI + trend */}
      <div
        className="grid grid-cols-1 gap-3 md:grid-cols-4"
        data-testid="gsc-panel-kpis"
      >
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <span className="text-xs font-medium text-slate-500">
            AI-surface impressions (28d)
          </span>
          <div
            className="mt-1 hub-stat__value"
            data-testid="gsc-headline-impressions"
          >
            {current.impressions.toLocaleString()}
          </div>
          <div className="mt-1 flex items-center gap-1 text-xs text-slate-500">
            {trend.arrow === 'up' && <Icon name="bx-trending-up" className="text-green-600" />}
            {trend.arrow === 'down' && <Icon name="bx-trending-down" className="text-red-600" />}
            {trend.arrow === 'flat' && <Icon name="bx-minus" className="text-slate-400" />}
            <span data-testid="gsc-headline-trend">{trend.label}</span>
            <span className="text-slate-400">vs prior 28d</span>
          </div>
        </div>

        {AI_SURFACES.map((surface) => {
          const d = byEngineCurrent[surface];
          const dim = d.impressions === 0;
          return (
            <div
              key={surface}
              data-testid={`gsc-surface-${surface}`}
              className={`rounded-lg border border-slate-200 bg-white p-5 ${dim ? 'opacity-60' : ''}`}
            >
              <span className="text-xs font-medium text-slate-500">
                {surfaceLabel(surface)}
              </span>
              <div className="mt-1 hub-stat__value">
                {d.impressions.toLocaleString()}
              </div>
              <div className="mt-1 grid grid-cols-3 gap-2 text-xs text-slate-500">
                <div>
                  <div className="text-xs">Clicks</div>
                  <div className="text-slate-700 font-medium">{d.clicks.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-xs">CTR</div>
                  <div className="text-slate-700 font-medium">{formatPct(d.ctr)}</div>
                </div>
                <div>
                  <div className="text-xs">Avg pos</div>
                  <div className="text-slate-700 font-medium">
                    {d.position == null ? '—' : d.position.toFixed(1)}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Top queries */}
      {topQueries.length > 0 && (
        <div data-testid="gsc-top-queries-card">
          <div className="text-xs font-medium text-slate-500 mb-2">
            Top queries by AI-surface impressions (28d)
          </div>
          <div className="overflow-x-auto">
            <table className="hub-table">
              <thead>
                <tr>
                  <th>Query</th>
                  <th className="is-num">Impressions</th>
                  <th className="is-num">Clicks</th>
                  <th className="is-num">CTR</th>
                  <th className="is-num">Avg pos</th>
                </tr>
              </thead>
              <tbody>
                {topQueries.map((q, i) => (
                  <tr key={`${q.query}-${i}`} data-testid="gsc-top-query-row">
                    <td>{q.query}</td>
                    <td className="is-num">
                      {q.impressions.toLocaleString()}
                    </td>
                    <td className="is-num">{q.clicks.toLocaleString()}</td>
                    <td className="is-num">{formatPct(q.ctr)}</td>
                    <td className="is-num">
                      {q.position == null ? '—' : q.position.toFixed(1)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function surfaceLabel(surface: AiSurface): string {
  switch (surface) {
    case 'ai_overview':
      return 'AI Overview';
    case 'ai_mode':
      return 'AI Mode';
    case 'featured_snippet':
      return 'Featured snippet';
  }
}
